import {Appearance, Column} from '../types';

const mergeAppearance = (parent?: Appearance, own?: Appearance): Appearance | undefined => {
  if (!parent || typeof parent !== 'object') return own;
  if (!own || typeof own !== 'object') return parent;
  return {...parent, ...own};
};

export interface ExpandedColumn extends Column {
  dataPath: string[];
  parentColumn?: Column;
}

export function expandColumns(columns: Column[], parentPath: string[] = [], depth: number = 0): ExpandedColumn[] {
  if (depth > 10) return [];
  const result: ExpandedColumn[] = [];
  const sortedColumns = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

  for (const column of sortedColumns) {
    const currentPath = [...parentPath, column.code];
    const shouldExpand = column.columnType === 'many-to-one' &&
                         column.relatedScreen?.columns &&
                         column.relatedScreen.columns.length > 0 &&
                         !column.editable;

    if (shouldExpand) {
      const visibleRelatedColumns = column.relatedScreen!.columns.filter(col => !col.screenHidden);
      const nestedColumns = expandColumns(visibleRelatedColumns, currentPath, depth + 1);
      for (const nestedCol of nestedColumns) {
        result.push({
          ...nestedCol,
          // The parent is never drawn itself, so its appearance is the default for what stands in for it;
          // the nested column's own keys win.
          appearance: mergeAppearance(column.appearance, nestedCol.appearance),
          parentColumn: column
        });
      }
    } else {
      result.push({
        ...column,
        dataPath: parentPath.length > 0 ? currentPath : [column.code]
      });
    }
  }

  return result;
}

export function hasSearchableColumn(columns: Column[]): boolean {
  for (const column of columns) {
    if (column.searchable) {
      return true;
    }
  }
  return false;
}

export function hasBarcodeSearch(columns: Column[], visitedScreenIds: Set<string> = new Set(), depth: number = 0): boolean {
  if (depth > 10) return false;
  for (const column of columns) {
    if (!column.searchable) {
      continue;
    }

    if (column.columnType === 'barcode') {
      return true;
    }

    if (column.targetColumn?.type === 'barcode') {
      return true;
    }

    if (column.relatedScreen?.columns) {
      const screenId = column.relatedScreen.id;
      if (screenId && !visitedScreenIds.has(screenId)) {
        visitedScreenIds.add(screenId);
        if (hasBarcodeSearch(column.relatedScreen.columns, visitedScreenIds, depth + 1)) {
          return true;
        }
      }
    }
  }
  return false;
}

export function getValueByPath(row: any, dataPath: string[]): any {
  if (!row || !dataPath || dataPath.length === 0) {
    return undefined;
  }

  let value = row;
  for (const key of dataPath) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = value[key];
  }

  return value;
}
